import { useState } from 'react';
import * as Yup from 'yup';
import { useAtom } from 'jotai';
import {
  CircularProgress,
  Box,
  FormControl,
  OutlinedInput,
  Select,
  MenuItem,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from '@mui/material';

import { UseCreateStores, useEditStore } from '../../Query/ManageStoreQuery';
import SelectStore from './AddStore';
import ImageUploadField from '../Utils/ImageUploadField';
import { Field, Form, Formik } from 'formik';
import { Toaster } from 'react-hot-toast';
import { useThemeColors } from '../../../hooks/useThemeColors';
import { storeAtom } from '../ManageRestaurantTable';
import MyEditor from '../../Stateless/RichTextEditor';
import LocationComponent from '../../Stateless/Location';
import MerchantNameField from './MerchantNameField';
import CategoryIdField from './CategoryIdField';
import StorePhoneNumberField from './StorePhoneNumberField';
import { COLORS } from '../../../theme/themeConstants';
import { alpha } from '@mui/material/styles';
import { splitPhoneNumber } from '../../HelperFunctions/validationUtils';


interface MerchantData {
  id: string;
  name: string;
}

interface CategoryData {
  id: string;
  nameEnglish: string;
}

// interface Option {
//     value: string;
//     label: string;
// }

const validationSchema = Yup.object().shape({
  merchantId: Yup.string().required('Please select a merchant'),
  categoryId: Yup.array().of(Yup.string()).min(1, 'Please select a category'),
  storeName: Yup.string()
    .required('Please enter store name')
    .max(50, 'Restaurant name cannot exceed 50 characters')
     .matches(
      /^[^0-9]+$/,
      'Store name cannot contain numbers'
    )
    .test(
      'no-leading-trailing-spaces',
      'Store name cannot start or end with spaces',
      (value) => value?.trim() === value
    )
    .test(
      'not-only-spaces',
      'Store name cannot be only spaces',
      (value) => value?.trim().length > 0
    )
    .test(
      'not-start-with-special',
      'Store name cannot start with a special character',
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if first character is a special character
        return !/^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(trimmed);
      }
    )
    .test(
      'not-only-special-chars',
      'Store name cannot consist only of special characters',
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if the string contains at least one letter or space (not just special chars)
        return /[a-zA-Z\s]/.test(trimmed);
      }
    ),
  storeNameArabic: Yup.string()
    .required('Please enter store name in Arabic')
    .max(50, 'Restaurant name cannot exceed 50 characters')
    .test(
      'no-leading-trailing-spaces',
      'Store name cannot start or end with spaces',
      (value) => value?.trim() === value
    )
    .test(
      'not-only-spaces',
      'Store name cannot be only spaces',
      (value) => value?.trim().length > 0
    )
   .matches(
      /^[\u0600-\u06FF\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/,
      'Store name must contain only Arabic letters, spaces, and special characters'
    )
    .matches(
      /^[^0-9]+$/,
      'Store name cannot contain numbers'
    )
    .test(
      'not-start-with-special',
      'Store name cannot start with a special character',
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if first character is a special character
        return !/^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(trimmed);
      }
    )
    .test(
      'not-only-special-chars',
      'Store name cannot consist only of special characters',
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if the string contains at least one Arabic letter or space (not just special chars)
        return /[\u0600-\u06FF\s]/.test(trimmed);
      }
    ),
  minOrderAmount: Yup.string()
    .required('Please enter minimum order amount')
    .test('is-number', 'Please enter a valid number', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0;
    }),
  canPreOrder: Yup.string().required('Please select pre-order option'),
  deliveryAvailable: Yup.string().required(
    'Please select deliveryAvailable option'
  ),
  hasSelfPickup: Yup.string().required('Please select hasSelfPickup option'),
  additionalPackagingRequest: Yup.string().required(
    'Please select Additional Packaging option'
  ),
  deliveryTime: Yup.string().when('deliveryAvailable', {
    is: (val: string) => val === 'Yes',
    then: (schema) =>
      schema
        .required('Please enter response time')
        .matches(/^\d+$/, 'Only numeric values allowed'),
    otherwise: (schema) => schema.notRequired(),
  }),
  // additionalCulinaryRequest: Yup.string().required(
  //   'Please select additional cutlery option'
  // ),
  aboutStore: Yup.string()
    .test(
      'has-content',
      'Please enter about store',
      value => {
        if (!value) return false;
        // Remove HTML tags and whitespace
        const text = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
        // Check for at least one letter or number
        return /[a-zA-Z0-9]/.test(text);
      }
    )
    .required('Please enter about store'),
  aboutStoreArabic: Yup.string()
    .test(
      'has-arabic-content',
      'Please enter valid Arabic content',
      value => {
        if (!value) return false;
        const text = value
          .replace(/<[^>]*>/g, '')      // Remove HTML tags
          .replace(/&nbsp;/g, '')       // Remove &nbsp;
          .trim();

        // Check for at least one Arabic character and only Arabic-compatible content
        const arabicRegex = /^[\u0600-\u06FF\s0-9،؛؟.,\-!()]+$/; // Includes Arabic letters, digits, common punctuation
        return arabicRegex.test(text);
      }
    )
    .required('Please enter about store in Arabic'),
  deliveryRadiusKM: Yup.string()
    .matches(/^\d+$/, 'Only numeric values allowed')
    .required('Please enter delivery radius'),
  storePhoneNumber: Yup.string()
    .required('Please enter the mobile number')
    .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
    .test(
      'len',
      'Mobile number must be 9 digits or more',
      (val) => val?.length >= 9
    )
    .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
  storeLogo: Yup.mixed()
    .required('Store logo is required')
    .test('fileSize', 'Logo must be less than 500kb', (value: any) => {
      if (!value || typeof value === 'string') return true;
      return value.size <= 500000;
    })
    .test(
      'dimensions',
      'Logo must be between 300x300px and 500x500px',
      (value: any): Promise<boolean> => {
        if (!value || !(value instanceof File)) return Promise.resolve(true);

        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;

            img.onload = () => {
              const { width, height } = img;
              if (
                width >= 300 &&
                height >= 300 &&
                width <= 500 &&
                height <= 500
              ) {
                resolve(true);
              } else {
                resolve(false);
              }
            };

            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(value);
        });
      }
    ),
  storeBanner: Yup.array()
    .min(1, 'At least one banner image is required')
    .max(6, 'Maximum 6 banner images allowed')
    .test(
      'fileSize',
      'Each banner must be less than 1MB',
      (value: any[] | undefined) => {
        if (!value || !Array.isArray(value)) return true;
        return value.every((file) => {
          if (typeof file === 'string') return true;
          return file.size <= 1 * 1024 * 1024;
        });
      }
    )
    .test(
      'dimensions',
      'Image dimensions must be between 1366×300 and 1500×500',
      async (value: any[] | undefined): Promise<boolean> => {
        if (!value || !Array.isArray(value)) return true;

        const dimensionChecks = await Promise.all(
          value.map(async (file) => {
            if (typeof file === 'string') return { isValid: true };
            if (!(file instanceof File)) return { isValid: true };

            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                  const { width, height } = img;
                  const isValid =
                    width >= 1366 &&
                    width <= 1500 &&
                    height >= 300 &&
                    height <= 500;
                  resolve({ isValid, width, height });
                };
                img.onerror = () => resolve({ isValid: false });
                img.src = event.target?.result as string;
              };
              reader.onerror = () => resolve({ isValid: false });
              try {
                reader.readAsDataURL(file);
              } catch (error) {
                console.error('Error reading file:', error);
                resolve({ isValid: false });
              }
            });
          })
        );

        const invalidDimensions = dimensionChecks
          .filter((check: any) => !check.isValid && check.width && check.height)
          .map((check: any) => `${check.width}×${check.height}`);

        if (invalidDimensions.length > 0) {
          return false;
        }

        return true;
      }
    ),
  workingHours: Yup.object({
    sunday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    monday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    tuesday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    wednesday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    thursday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    friday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    saturday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
  }),

   deliveryTimeUnit: Yup.string().when('deliveryAvailable', {
      is: (val: string) => val === 'Yes',
      then: (schema) => schema.required('Please select time unit'),
      otherwise: (schema) => schema.notRequired(),
    }),
  storeAddress: Yup.string().required('Please enter store address'),
  latitude: Yup.string().required('Latitude is required'),
  longitude: Yup.string().required('Longitude is required'),
}).test(
  'pickup-or-delivery',
  'Either Self Pickup or Delivery Available must be Yes',
  function (value) {
    const hasSelfPickup = (value as any)?.hasSelfPickup;
    const deliveryAvailable = (value as any)?.deliveryAvailable;
    if (hasSelfPickup === 'No' && deliveryAvailable === 'No') {
      return this.createError({
        path: 'deliveryAvailable',
        message: 'Either Self Pickup or Delivery Available must be Yes',
      });
    }
    return true;
  }
);

const generateTimeOptions = () => {
  const times = [];
  for (let hours = 0; hours < 24; hours++) {
    for (let minutes = 0; minutes < 60; minutes += 30) {
      const hour = hours.toString().padStart(2, '0');
      const minute = minutes.toString().padStart(2, '0');
      times.push(`${hour}:${minute}`);
    }
  }
  // Add 23:59 as an additional option
  if (!times.includes('23:59')) {
    times.push('23:59');
  }
  return times;
};
type AddRestaurantProps = {
  onCancel: () => void;
  storeType: string;
  edit?: string;
  merchantList?: any[];
  categoryData?: any[];
};

const AddPharmacy = ({ onCancel, storeType, edit, merchantList, categoryData }: AddRestaurantProps) => {
  const { warning } = useThemeColors();
  // const [open, setOpen] = useState(false);
  const [storeId] = useAtom<any>(storeAtom);

  // const countryCodes = [
  //   '+971', '+966', '+965', '+973', '+974', '+968', '+20', '+962', '+961', '+964', '+92'
  // ];
  // const fullNumber = storeId?.restaurantDetails?.storePhoneNumber || '';
  // const matchedCode = countryCodes.find(code => fullNumber.startsWith(code));
  // const phoneNumber = matchedCode ? fullNumber.slice(matchedCode.length) : '';
  const {countryCode:matchedCode, phone:phoneNumber} = splitPhoneNumber(storeId?.restaurantDetails?.storePhoneNumber)
  const [selectedCountryCode, setSelectedCountryCode] = useState(matchedCode || '+962');

  const { mutate: EditStore } = useEditStore();
  const { mutate: CreateStore, isPending: isCreateStoreLoading } =
    UseCreateStores();

  const merchantOptions = merchantList?.map((item: MerchantData) => ({
    value: item.id,
    label: item.name,
  }));

  const categoryOptions = categoryData?.map((item: CategoryData) => ({
    value: item.id,
    label: item.nameEnglish,
  }));


  const categoryIds = storeId?.restaurantDetails?.categoryId || [];

  const initialValues = {
    preferableTime: storeId ? storeId.restaurantDetails.preferableTime.map((item: string) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase()) : [],
    categoryId: storeId ? categoryIds : [],
    storeAddress: storeId
      ? storeId?.restaurantDetails?.storeAddress?.address
      : '',
    storeName: storeId ? storeId.nameEnglish : '',
    merchantId: storeId ? storeId.restaurantDetails.merchantId : '',

    storeNameArabic: storeId ? storeId.nameArabic : '',
    minOrderAmount: storeId
      ? storeId.restaurantDetails.minOrderAmount.value
      : null,
    canPreOrder: storeId
      ? storeId.restaurantDetails.canPreOrder
        ? 'Yes'
        : 'No'
      : '',
    additionalPackagingRequest: storeId
      ? storeId.restaurantDetails.canPreOrder
        ? 'Yes'
        : 'No'
      : '',

    hasSelfPickup: storeId
      ? storeId.restaurantDetails.hasSelfPickup
        ? 'Yes'
        : 'No'
      : '',
    deliveryAvailable: storeId
      ? storeId.restaurantDetails.deliveryAvailable
        ? 'Yes'
        : 'No'
      : '',
    deliveryTime: storeId
      ? storeId.restaurantDetails.deliveryTime.value
      : '',
    deliveryTimeUnit:
      storeId &&
      storeId.restaurantDetails &&
      storeId.restaurantDetails.deliveryTime &&
      storeId.restaurantDetails.deliveryTime.format
        ? storeId.restaurantDetails.deliveryTime.format.charAt(0).toUpperCase() +
          storeId.restaurantDetails.deliveryTime.format.slice(1)
        : 'Minutes',
    aboutStore: storeId ? storeId.restaurantDetails.aboutStore : '',
    storePhoneNumber: phoneNumber,
    aboutStoreArabic: storeId
      ? storeId.restaurantDetails.aboutStoreArabic
      : '',
    deliveryRadiusKM: storeId
      ? storeId.restaurantDetails.deliveryRadiusKM
      : '',
    // storePhoneNumber: storeId
    //   ? storeId.restaurantDetails.storePhoneNumber
    //   : '',

    storeLogo: storeId ? storeId.restaurantDetails.storeLogo : '',
    storeBanner: storeId ? storeId.restaurantDetails.storeBanner : [],
    latitude: storeId
      ? storeId.restaurantDetails.storeAddress.latitude
      : '',
    longitude: storeId
      ? storeId.restaurantDetails.storeAddress.longitude
      : '',
    location: storeId
      ? {
        address: storeId?.restaurantDetails?.storeAddress.address,
        latitude: storeId?.restaurantDetails?.storeAddress.latitude,
        longitude: storeId?.restaurantDetails?.storeAddress.longitude,
      }
      : {
        address: '',
        latitude: '',
        longitude: '',
      },
    workingHours: {
      sunday: storeId?.restaurantDetails?.workingHours?.sunday || {
        isOpen: true,
        startTime: '',
        endTime: '',
      },
      monday: storeId?.restaurantDetails?.workingHours?.monday || {
        isOpen: true,
        startTime: '',
        endTime: '',
      },
      tuesday: storeId?.restaurantDetails?.workingHours?.tuesday || {
        isOpen: true,
        startTime: '',
        endTime: '',
      },
      wednesday: storeId?.restaurantDetails?.workingHours?.wednesday || {
        isOpen: true,
        startTime: '',
        endTime: '',
      },
      thursday: storeId?.restaurantDetails?.workingHours?.thursday || {
        isOpen: true,
        startTime: '',
        endTime: '',
      },
      friday: storeId?.restaurantDetails?.workingHours?.friday || {
        isOpen: true,
        startTime: '',
        endTime: '',
      },
      saturday: storeId?.restaurantDetails?.workingHours?.saturday || {
        isOpen: true,
        startTime: '',
        endTime: '',
      },
    },
    deliveryFee: storeId?.restaurantDetails?.deliveryFee || {
      deliveryChargeSetting: {
        type: 'default',
        deliveryFeeStatus: {
          type: 'disable',
          commonDeliveryFee: { type: 'disable', commonDeliveryFeeValue: '' },
          feePerKM: { type: 'disable', feePerKMValue: '' },
        },
        peakHourDeliveryFeeStatus: {
          type: 'disable',
          data: {
            sunday: [],
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
          },
        },
      },
    },
  };

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    try {
      // Ensure deliveryFee values are numbers and default to 0 if empty or invalid
      const deliveryFee = { ...values.deliveryFee };
      const deliveryChargeSetting = deliveryFee?.deliveryChargeSetting || {};
      const deliveryFeeStatus = deliveryChargeSetting?.deliveryFeeStatus || {};
      if (deliveryFeeStatus.commonDeliveryFee) {
        deliveryFeeStatus.commonDeliveryFee.commonDeliveryFeeValue =
          deliveryFeeStatus.commonDeliveryFee.commonDeliveryFeeValue === '' ||
            deliveryFeeStatus.commonDeliveryFee.commonDeliveryFeeValue === undefined ||
            deliveryFeeStatus.commonDeliveryFee.commonDeliveryFeeValue === null ||
            isNaN(Number(deliveryFeeStatus.commonDeliveryFee.commonDeliveryFeeValue))
            ? 0
            : Number(deliveryFeeStatus.commonDeliveryFee.commonDeliveryFeeValue);
      }
      if (deliveryFeeStatus.feePerKM) {
        deliveryFeeStatus.feePerKM.feePerKMValue =
          deliveryFeeStatus.feePerKM.feePerKMValue === '' ||
            deliveryFeeStatus.feePerKM.feePerKMValue === undefined ||
            deliveryFeeStatus.feePerKM.feePerKMValue === null ||
            isNaN(Number(deliveryFeeStatus.feePerKM.feePerKMValue))
            ? 0
            : Number(deliveryFeeStatus.feePerKM.feePerKMValue);
      }
      deliveryChargeSetting.deliveryFeeStatus = deliveryFeeStatus;
      deliveryFee.deliveryChargeSetting = deliveryChargeSetting;

      const formattedData: any = {
        ...values,
        storePhoneNumber: `${selectedCountryCode}${values.storePhoneNumber}`,
        storeName: values.storeName,
        storeNameArabic: values.storeNameArabic,
        minOrderAmount: {
          currency: 'JOD',
          value: +values.minOrderAmount,
        },
        canPreOrder: values.canPreOrder === 'Yes',
        additionalPackagingRequest:
          values.additionalPackagingRequest === 'Yes',
        preferableTime: values.preferableTime.map((item: string) => item.toLowerCase()),
        storeType: storeType,
        hasSelfPickup: values.hasSelfPickup === 'Yes',
        deliveryAvailable: values.deliveryAvailable === 'Yes',

        aboutStore: values.aboutStore,
        aboutStoreArabic: values.aboutStoreArabic,
        storeAddress: {
          address: values.storeAddress || '',
          latitude: values.latitude || '',
          longitude: values.longitude || '',
        },
        workingHours: values.workingHours,
        deliveryRadiusKM: values.deliveryRadiusKM,
        storeLogo: values.storeLogo,
        storeBanner: values.storeBanner,
      };

      // Include or omit delivery-specific fields
      if (values.deliveryAvailable === 'Yes') {
        formattedData.deliveryTime = {
          value: +values.deliveryTime,
          format: values?.deliveryTimeUnit?.toLowerCase(),
        };
        formattedData.deliveryFee = deliveryFee;
      } else {
        delete formattedData.deliveryTime;
        delete formattedData.deliveryFee;
      }
      if (storeId) {
        EditStore(
          {
            id: storeId.id,
            data: formattedData,
          },
          {
            onSuccess: () => {
              onCancel();
            },
            onError: (error: any) => {
              console.error('EditStore error:', error);
            },
          }
        );
      } else {
        CreateStore(formattedData, {
          onSuccess: () => {
            onCancel();
          },
          onError: (error: any) => {
            console.error('CreateStore error:', error);
          },
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const logAndValidate = async (values: typeof initialValues) => {
    try {
      // Use Yup's validation schema to validate
      await validationSchema.validate(values, { abortEarly: false });
      return {};
    } catch (err: any) {
      if (err && err.inner) {
        const errorMap: Record<string, string> = {};
        err.inner.forEach((validationError: any) => {
          if (validationError.path && !errorMap[validationError.path]) {
            errorMap[validationError.path] = validationError.message;
          }
        });
        // Log all errors to the console
        console.error('Formik Validation Errors:', errorMap);
        return errorMap;
      }
      // Log any other error
      console.error('Formik Validation Error:', err);
      return {};
    }
  };


  return (
    <>
      <Toaster position="top-right" />
      {edit && (
        <SelectStore onCancel={onCancel} storeType={storeType} />
      )}
      <Box
        maxWidth={800}
        mx="auto"
        my={4}
        p={{ xs: 2, sm: 4 }}
        bgcolor="#fff"
        borderRadius={3}
        boxShadow={2}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
          validate={logAndValidate}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue, setFieldTouched, submitCount }) => (
            <Form>
              {/* Merchant Name */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Merchant Name <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Field name="merchantId" component={MerchantNameField} merchantOptions={merchantOptions} />
                </Box>
              </Box>
              {/* Category */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Category <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Field name="categoryId" component={CategoryIdField} storeId={storeId} categoryOptions={categoryOptions} />
                </Box>
              </Box>
              {/* Store Name */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Store Name <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.storeName && Boolean(errors.storeName)} fullWidth>
                    <OutlinedInput
                      name="storeName"
                      value={values.storeName}
                      onChange={(e) => {
                          const newValue = e.target.value;
                          // Filter to disallow numbers
                          let filteredValue = newValue.replace(/[0-9]/g, '');
                          
                          // Prevent starting with special characters
                          if (filteredValue.length > 0 && /^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(filteredValue)) {
                            filteredValue = filteredValue.substring(1);
                          }
                          
                          if (filteredValue.length <= 50) {
                            setFieldValue('storeName', filteredValue);
                            setFieldTouched('storeName', true, false);
                          } else {
                            setFieldValue('storeName', filteredValue.slice(0, 50));
                          }
                        }}
                      onBlur={() => setFieldTouched('storeName', true)}
                      placeholder="Enter Restaurant Name"
                      sx={{ width: { xs: '100%', sm: '400px' } }}
                    />
                  </FormControl>
                  {touched.storeName && typeof errors.storeName === 'string' && (
                    <Typography variant="caption" color="error">{errors.storeName}</Typography>
                  )}
                </Box>
              </Box>
              {/* Store Name Arabic */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Store Name (Arabic) <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.storeNameArabic && Boolean(errors.storeNameArabic)} fullWidth>
                    <OutlinedInput
                      name="storeNameArabic"
                      value={values.storeNameArabic}
                      onChange={(e) => {
                          const newValue = e.target.value;
                          // Filter to disallow numbers
                          let filteredValue = newValue.replace(/[0-9]/g, '');
                          
                          // Prevent starting with special characters
                          if (filteredValue.length > 0 && /^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(filteredValue)) {
                            filteredValue = filteredValue.substring(1);
                          }
                          
                          if (filteredValue.length <= 50) {
                            setFieldValue('storeNameArabic', filteredValue);
                            setFieldTouched('storeNameArabic', true, false);
                          } else {
                            setFieldValue('storeNameArabic', filteredValue.slice(0, 50));
                          }
                        }}
                      onBlur={() => setFieldTouched('storeNameArabic', true)}
                      placeholder="Enter Restaurant Name in Arabic"
                      sx={{ width: { xs: '100%', sm: '400px' } }}
                    />
                  </FormControl>
                  {touched.storeNameArabic && typeof errors.storeNameArabic === 'string' && (
                    <Typography variant="caption" color="error">{errors.storeNameArabic}</Typography>
                  )}
                </Box>
              </Box>
              {/* Minimum Order Amount */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Minimum Order Amount <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.minOrderAmount && Boolean(errors.minOrderAmount)} fullWidth>
                    <Box sx={{ display: 'flex', gap: '4px', width: { xs: '100%', sm: '400px' } }}>
                      <Box sx={{ width: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>JOD</Box>
                      <OutlinedInput
                        type="number"
                        value={values.minOrderAmount}
                        onChange={(e) => {
                          setFieldValue('minOrderAmount', e.target.value);
                          setFieldTouched('minOrderAmount', true, false);
                        }}
                        placeholder="Enter Minimum Order Amount"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </FormControl>
                  {touched.minOrderAmount && typeof errors.minOrderAmount === 'string' && (
                    <Typography variant="caption" color="error">{errors.minOrderAmount}</Typography>
                  )}
                </Box>
              </Box>
              {/* Pre-order Option */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Pre-order Option <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.canPreOrder && Boolean(errors.canPreOrder)} fullWidth>
                    <RadioGroup
                      row
                      name="canPreOrder"
                      value={values.canPreOrder}
                      onChange={(e) => setFieldValue('canPreOrder', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                  {touched.canPreOrder && typeof errors.canPreOrder === 'string' && (
                    <Typography variant="caption" color="error">{errors.canPreOrder}</Typography>
                  )}
                </Box>
              </Box>
              {/* additional packaging request */}
              <Box
                display="flex"
                alignItems="center"
                mb={2}
                sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}
              >
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">
                    Additional Package Request <span style={{ color: 'red' }}>*</span>
                  </Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.additionalPackagingRequest && Boolean(errors.additionalPackagingRequest)} fullWidth>
                    <RadioGroup
                      row
                      name="additionalPackagingRequest"
                      value={values.additionalPackagingRequest}
                      onChange={(e) => setFieldValue('additionalPackagingRequest', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                  {touched.additionalPackagingRequest && typeof errors.additionalPackagingRequest === 'string' && (
                    <Typography variant="caption" color="error">
                      {errors.additionalPackagingRequest}
                    </Typography>
                  )}
                </Box>
              </Box>
              {/* Self Pickup */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Self Pickup <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.hasSelfPickup && Boolean(errors.hasSelfPickup)} fullWidth>
                    <RadioGroup
                      row
                      name="hasSelfPickup"
                      value={values.hasSelfPickup}
                      onChange={(e) => setFieldValue('hasSelfPickup', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                  {touched.hasSelfPickup && typeof errors.hasSelfPickup === 'string' && (
                    <Typography variant="caption" color="error">{errors.hasSelfPickup}</Typography>
                  )}
                </Box>
              </Box>
              {/* Delivery Available */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Delivery Available <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.deliveryAvailable && Boolean(errors.deliveryAvailable)} fullWidth>
                    <RadioGroup
                      row
                      name="deliveryAvailable"
                      value={values.deliveryAvailable}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFieldValue('deliveryAvailable', val);
                        if (val === 'No') {
                          setFieldValue('deliveryTime', '');
                          setFieldValue('deliveryTimeUnit', '');
                        }
                      }}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                  {touched.deliveryAvailable && typeof errors.deliveryAvailable === 'string' && (
                    <Typography variant="caption" color="error">{errors.deliveryAvailable}</Typography>
                  )}
                </Box>
              </Box>
              {/* Delivery fee settings now always use the platform default configuration,
                  so we no longer show a dedicated delivery fee section here. */}
              {/* Response Time and Unit (combined row) */}
             {values.deliveryAvailable === 'Yes' && (
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Response Time <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', sm: '400px' } }}>
                      <FormControl error={touched.deliveryTime && Boolean(errors.deliveryTime)} sx={{ flex: 1 }}>
                        <OutlinedInput
                          name="deliveryTime"
                          value={values.deliveryTime}
                          onChange={(e) => setFieldValue('deliveryTime', e.target.value)}
                          onBlur={() => setFieldTouched('deliveryTime', true)}
                          onKeyDown={(e) => {
                            const isDigit = /^[0-9]$/.test(e.key);
                            const allowedKeys = [
                              'Backspace',
                              'Delete',
                              'Tab',
                              'ArrowLeft',
                              'ArrowRight',
                            ];
                            if (!isDigit && !allowedKeys.includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          placeholder="Enter Time"
                        />
                      </FormControl>
                      <FormControl error={touched.deliveryTimeUnit && Boolean(errors.deliveryTimeUnit)} sx={{ minWidth: 120 }}>
                        <Select
                          value={values.deliveryTimeUnit}
                          onChange={(e) => setFieldValue('deliveryTimeUnit', e.target.value)}
                          displayEmpty
                          input={<OutlinedInput />}
                        >
                          <MenuItem value="">
                            <em>Select Unit</em>
                          </MenuItem>
                          <MenuItem value="Minutes">Minutes</MenuItem>
                          <MenuItem value="Hours">Hours</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {(touched.deliveryTime && typeof errors.deliveryTime === 'string') && (
                      <Typography variant="caption" color="error">{errors.deliveryTime}</Typography>
                    )}
                    {(touched.deliveryTimeUnit && typeof errors.deliveryTimeUnit === 'string') && (
                      <Typography variant="caption" color="error">{errors.deliveryTimeUnit}</Typography>
                    )}
                  </Box>
                </Box>
              )}
              {/* About Store */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">About Store <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <MyEditor
                    value={values.aboutStore}
                    onChange={(val: string) => setFieldValue('aboutStore', val)}
                    onBlur={() => setFieldTouched('aboutStore', true)}
                  />
                  {((touched.aboutStore || submitCount > 0) && typeof errors.aboutStore === 'string') && (
                    <Typography variant="caption" color="error">{errors.aboutStore}</Typography>
                  )}
                </Box>
              </Box>
              {/* About Store Arabic */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">About Store (Arabic) <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <MyEditor
                    value={values.aboutStoreArabic}
                    onChange={(val: string) => setFieldValue('aboutStoreArabic', val)}
                    onBlur={() => setFieldTouched('aboutStoreArabic', true)}
                  />
                  {((touched.aboutStoreArabic || submitCount > 0) && typeof errors.aboutStoreArabic === 'string') && (
                    <Typography variant="caption" color="error">{errors.aboutStoreArabic}</Typography>
                  )}
                </Box>
              </Box>
              {/* Delivery Radius */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Delivery Radius (KM) <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.deliveryRadiusKM && Boolean(errors.deliveryRadiusKM)} fullWidth>
                    <OutlinedInput
                      name="deliveryRadiusKM"
                      value={values.deliveryRadiusKM}
                      onChange={(e) => setFieldValue('deliveryRadiusKM', e.target.value)}
                      onBlur={() => setFieldTouched('deliveryRadiusKM', true)}
                      onKeyDown={(e) => {
                        const isDigit = /^[0-9]$/.test(e.key);
                        const allowedKeys = [
                          'Backspace',
                          'Delete',
                          'Tab',
                          'ArrowLeft',
                          'ArrowRight',
                        ];
                        if (!isDigit && !allowedKeys.includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="Enter Delivery Radius"
                      sx={{ width: { xs: '100%', sm: '400px' } }}
                    />

                  </FormControl>
                  {touched.deliveryRadiusKM && typeof errors.deliveryRadiusKM === 'string' && (
                    <Typography variant="caption" color="error">{errors.deliveryRadiusKM}</Typography>
                  )}
                </Box>
              </Box>
              {/* Store Phone Number */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Store Phone Number <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Field
                    name="storePhoneNumber"
                    component={StorePhoneNumberField}
                    selectedCountryCode={selectedCountryCode}
                    onCountryCodeChange={setSelectedCountryCode}
                  />
                </Box>
              </Box>
              {/* Store Address */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Store Address <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  {/* <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Store Address</Typography> */}
                  <LocationComponent
                    field={{ name: 'storeAddress', value: values.storeAddress }}
                    form={{ values, errors, touched, setFieldValue, setFieldTouched }}
                  />
                </Box>
              </Box>
              {/* Store Logo */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Store Logo <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Box mt={2}>
                    <ImageUploadField name="storeLogo" label="Store Logo" />
                    {(touched.storeLogo || values.storeLogo) && typeof errors.storeLogo === 'string' && (
                      <Typography variant="caption" color="error">{errors.storeLogo}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              {/* Store Banner */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">banner <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Box mt={2}>
                    <ImageUploadField name="storeBanner" label="banner" multiple maxFiles={6} recommendedText="Max 6 images" />
                    {(touched.storeBanner || (values.storeBanner && values.storeBanner.length > 0)) && typeof errors.storeBanner === 'string' && (
                      <Typography variant="caption" color="error">{errors.storeBanner}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              {/* Working Hours */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Working Hours <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl fullWidth>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      {(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const).map((dayKey) => {
                        const dayData = values.workingHours[dayKey];
                        const dayErrors = (errors?.workingHours && (errors.workingHours as any)[dayKey]) || {};
                        const dayTouched = (touched?.workingHours && (touched.workingHours as any)[dayKey]) || {};
                        return (
                          <Box key={dayKey} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ minWidth: { xs: 80, sm: 100 }, maxWidth: { xs: 120, sm: 140 }, mr: 1, display: 'flex', alignItems: 'center' }}>
                              <Typography sx={{ color: 'text.secondary', fontSize: 14, whiteSpace: 'nowrap' }}>{dayKey.charAt(0).toUpperCase() + dayKey.slice(1)} *</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!dayData.isOpen}
                                    onChange={(e) => {
                                      setFieldValue(`workingHours.${dayKey}.isOpen`, !e.target.checked);
                                      if (e.target.checked) {
                                        setFieldValue(`workingHours.${dayKey}.startTime`, '00:00:00Z');
                                        setFieldValue(`workingHours.${dayKey}.endTime`, '00:00:00Z');
                                      }
                                      setFieldTouched(`workingHours.${dayKey}`, true, false);
                                    }}
                                    size="small"
                                  />
                                }
                                label={<Typography variant="body2" color="text.secondary">Closed</Typography>}
                              />
                              {dayData.isOpen && (
                                <Box sx={{ display: 'flex', gap: 4 }}>
                                  {/* Start Time Select */}
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControl error={Boolean(dayTouched?.startTime && dayErrors?.startTime)} size="small" variant="outlined" sx={{ minWidth: 120, mr: 2 }}>
                                      <Select
                                        value={dayData.startTime ? dayData.startTime.replace(':00Z', '') : ''}
                                        onChange={(e) => {
                                          setFieldValue(`workingHours.${dayKey}.startTime`, e.target.value ? `${e.target.value}:00Z` : '00:00:00Z');
                                          setFieldTouched(`workingHours.${dayKey}.startTime`, true, false);
                                        }}
                                        onBlur={() => setFieldTouched(`workingHours.${dayKey}.startTime`, true, false)}
                                        displayEmpty
                                      >
                                        <MenuItem value="">
                                          <em>Select Start</em>
                                        </MenuItem>
                                        {generateTimeOptions().map((time) => (
                                          <MenuItem key={time} value={time}>{time}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    {dayTouched?.startTime && typeof dayErrors?.startTime === 'string' && (
                                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{dayErrors.startTime}</Typography>
                                    )}
                                  </Box>
                                  {/* End Time Select */}
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControl error={Boolean(dayTouched?.endTime && dayErrors?.endTime)} size="small" variant="outlined" sx={{ minWidth: 120, mr: 2 }}>
                                      <Select
                                        value={dayData.endTime ? dayData.endTime.replace(':00Z', '') : ''}
                                        onChange={(e) => {
                                          setFieldValue(`workingHours.${dayKey}.endTime`, e.target.value ? `${e.target.value}:00Z` : '00:00:00Z');
                                          setFieldTouched(`workingHours.${dayKey}.endTime`, true, false);
                                        }}
                                        onBlur={() => setFieldTouched(`workingHours.${dayKey}.endTime`, true, false)}
                                        displayEmpty
                                      >
                                        <MenuItem value="">
                                          <em>Select End</em>
                                        </MenuItem>
                                        {generateTimeOptions().map((time) => (
                                          <MenuItem key={time} value={time}>{time}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    {dayTouched?.endTime && typeof dayErrors?.endTime === 'string' && (
                                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{dayErrors.endTime}</Typography>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </FormControl>
                </Box>
              </Box>
              {/* Buttons */}
              <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', justifyContent: 'center', width: '100%', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: 'none', fontSize: 14, px: 3, py: 1, backgroundColor: COLORS.primary.main, '&:hover': { backgroundColor: alpha(COLORS.primary.main, 0.8) }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }}
                  disabled={isSubmitting}
                >
                  {isCreateStoreLoading ? (
                    <>
                      <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                      {storeId ? 'Updating...' : 'Creating...'}
                    </>
                  ) : storeId ? (
                    'Update'
                  ) : (
                    'Create'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  onClick={onCancel}
                  sx={{ bgcolor: warning.main, color: '#fff', '&:hover': { bgcolor: warning.dark, color: '#fff' } }}
                >
                  Cancel
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default AddPharmacy;
